import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "./Layout";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Edit, 
  Building,
  Trash2
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HotelsPage = ({ user, onLogout }) => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [hotelToDelete, setHotelToDelete] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [formData, setFormData] = useState({
    hotel_code: "",
    hotel_name: "",
    status: "active"
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchHotels = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/hotels`);
      setHotels(response.data);
    } catch (error) {
      console.error("Error fetching hotels:", error);
      toast.error("Failed to fetch hotels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotels();
  }, []);

  const filteredHotels = hotels.filter(hotel =>
    hotel.hotel_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    hotel.hotel_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      hotel_code: "",
      hotel_name: "",
      status: "active"
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.hotel_code.trim()) newErrors.hotel_code = "Hotel code is required";
    if (!formData.hotel_name.trim()) newErrors.hotel_name = "Hotel name is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleAddHotel = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/hotels`, formData);
      toast.success("Hotel added successfully");
      setShowAddDialog(false);
      resetForm();
      fetchHotels();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to add hotel";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditHotel = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/hotels/${selectedHotel.id}`, formData);
      toast.success("Hotel updated successfully");
      setShowEditDialog(false);
      resetForm();
      setSelectedHotel(null);
      fetchHotels();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to update hotel";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (hotel) => {
    setSelectedHotel(hotel);
    setFormData({
      hotel_code: hotel.hotel_code,
      hotel_name: hotel.hotel_name,
      status: hotel.status
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (hotel) => {
    setHotelToDelete(hotel);
    setShowDeleteDialog(true);
  };

  const handleDeleteHotel = async () => {
    if (!hotelToDelete) return;
    
    setSubmitting(true);
    try {
      await axios.delete(`${API}/hotels/${hotelToDelete.id}`);
      toast.success("Hotel deleted successfully");
      setShowDeleteDialog(false);
      setHotelToDelete(null);
      fetchHotels();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to delete hotel";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    return status === "active" ? "status-badge status-active" : "status-badge status-inactive";
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="page-header">
        <h2 className="page-title">Hotels Management</h2>
        <p className="page-description">
          Manage your hotel catalog and properties
        </p>
      </div>

      {/* Search and Add */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div className="card-content">
          <div className="search-filters">
            <div className="search-input">
              <div style={{ position: "relative" }}>
                <Search 
                  size={20} 
                  style={{ 
                    position: "absolute", 
                    left: "0.75rem", 
                    top: "50%", 
                    transform: "translateY(-50%)", 
                    color: "#6b7280" 
                  }} 
                />
                <Input
                  placeholder="Search hotels by name or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: "2.5rem" }}
                  data-testid="search-input"
                />
              </div>
            </div>

            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => {
                    resetForm();
                    setShowAddDialog(true);
                  }}
                  data-testid="add-hotel-button"
                >
                  <Plus size={20} style={{ marginRight: "0.5rem" }} />
                  Add Hotel
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Hotel</DialogTitle>
                </DialogHeader>
                <HotelForm
                  formData={formData}
                  errors={errors}
                  onInputChange={handleInputChange}
                  onSubmit={handleAddHotel}
                  onCancel={() => setShowAddDialog(false)}
                  submitting={submitting}
                  isEdit={false}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Hotels Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Hotels ({filteredHotels.length})</h3>
        </div>
        <div className="card-content" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: "3rem", textAlign: "center" }}>
              <div className="loading-spinner" style={{ margin: "0 auto" }}></div>
              <p style={{ marginTop: "1rem", color: "#6b7280" }}>Loading hotels...</p>
            </div>
          ) : filteredHotels.length === 0 ? (
            <div className="empty-state">
              <Building className="empty-state-icon" />
              <h3 className="empty-state-title">No hotels found</h3>
              <p className="empty-state-description">
                {searchTerm
                  ? "Try adjusting your search term"
                  : "Get started by adding your first hotel"
                }
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Hotel Code</th>
                    <th>Hotel Name</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHotels.map((hotel) => (
                    <tr key={hotel.id}>
                      <td style={{ fontWeight: "600" }}>{hotel.hotel_code}</td>
                      <td>{hotel.hotel_name}</td>
                      <td>
                        <span className={getStatusBadgeClass(hotel.status)}>
                          {hotel.status}
                        </span>
                      </td>
                      <td>
                        {new Date(hotel.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => openEditDialog(hotel)}
                            className="btn-icon edit"
                            title="Edit hotel"
                            data-testid={`edit-hotel-${hotel.id}`}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => openDeleteDialog(hotel)}
                            className="btn-icon delete"
                            title="Delete hotel"
                            data-testid={`delete-hotel-${hotel.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Hotel</DialogTitle>
          </DialogHeader>
          <HotelForm
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
            onSubmit={handleEditHotel}
            onCancel={() => {
              setShowEditDialog(false);
              resetForm();
              setSelectedHotel(null);
            }}
            submitting={submitting}
            isEdit={true}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Hotel</DialogTitle>
          </DialogHeader>
          <div>
            <p style={{ marginBottom: "1.5rem" }}>
              Are you sure you want to delete <strong>{hotelToDelete?.hotel_name}</strong>? 
              This action cannot be undone and will only work if the hotel has no associated executives or clients.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteDialog(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteHotel}
                disabled={submitting}
                data-testid="confirm-delete-hotel"
              >
                {submitting ? (
                  <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <div className="loading-spinner"></div>
                    Deleting...
                  </span>
                ) : (
                  "Delete Hotel"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

// Hotel Form Component
const HotelForm = ({ 
  formData, 
  errors, 
  onInputChange, 
  onSubmit, 
  onCancel, 
  submitting, 
  isEdit 
}) => (
  <div>
    <div style={{ display: "grid", gap: "1rem" }}>
      <div>
        <Label htmlFor="hotel_code">Hotel Code *</Label>
        <Input
          id="hotel_code"
          value={formData.hotel_code}
          onChange={(e) => onInputChange("hotel_code", e.target.value)}
          className={errors.hotel_code ? "error" : ""}
          placeholder="e.g., HTL001"
          data-testid="hotel-code-input"
        />
        {errors.hotel_code && <div className="error-message">{errors.hotel_code}</div>}
      </div>

      <div>
        <Label htmlFor="hotel_name">Hotel Name *</Label>
        <Input
          id="hotel_name"
          value={formData.hotel_name}
          onChange={(e) => onInputChange("hotel_name", e.target.value)}
          className={errors.hotel_name ? "error" : ""}
          placeholder="e.g., Grand Hotel Plaza"
          data-testid="hotel-name-input"
        />
        {errors.hotel_name && <div className="error-message">{errors.hotel_name}</div>}
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select 
          value={formData.status} 
          onValueChange={(value) => onInputChange("status", value)}
        >
          <SelectTrigger data-testid="hotel-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "1.5rem" }}>
      <Button variant="outline" onClick={onCancel} disabled={submitting}>
        Cancel
      </Button>
      <Button onClick={onSubmit} disabled={submitting} data-testid="submit-hotel-button">
        {submitting ? (
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div className="loading-spinner"></div>
            {isEdit ? "Updating..." : "Adding..."}
          </span>
        ) : (
          isEdit ? "Update Hotel" : "Add Hotel"
        )}
      </Button>
    </div>
  </div>
);

export default HotelsPage;